import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'reactstrap';
import {
  withLoading,
  useAppContext,
  useTranslator,
  removeAuthCredentials,
  useToast,
} from '@front-activerse/common-components';
import Layout from '../../Layout/Layout';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';

import { useOutletContext } from 'react-router-dom';
import { sha256 } from 'js-sha256';

const BuyThankYouContainer = ({ reportPayment, token }) => {
  const [userEmail, setUserEmail] = useState();
  const { t, language } = useTranslator();
  const { trackFirebaseEvent } = useOutletContext();
  const { toastError } = useToast();
  const [isPast48Hours, setIsPast48Hours] = useState(false);

  const {
    images: {
      appStore,
      googlePlay,
      thankYouPageBg,
      thankYouPageBgMobile,
      thankYouPageAppEn,
      thankYouPageAppSe,
      thankYouPageLogInEn,
      thankYouPageLogInSe,
      thankYouPageLogInPl,
      thankYouPageAppPl,
      thankYouPageLogInPt,
      thankYouPageAppPt,
    },
    consts: { GOOGLE_PLAY_URL, APP_STORE_URL },
  } = useAppContext();

  const imageMap = {
    'en-US': {
      logIn: thankYouPageLogInEn,
      enjoyApp: thankYouPageAppEn,
    },
    'sv-SE': {
      logIn: thankYouPageLogInSe,
      enjoyApp: thankYouPageAppSe,
    },
    'pl-PL': {
      logIn: thankYouPageLogInPl,
      enjoyApp: thankYouPageAppPl,
    },
    'pt-BR': {
      logIn: thankYouPageLogInPt,
      enjoyApp: thankYouPageAppPt,
    },
  };

  const renderImage = (name) => imageMap[language]?.[name] || null;

  const handleIsPast48Hours = (created) => {
    const now = new Date();
    const userCreatedDate = new Date(created);
    const timeDifference = +now - +userCreatedDate;

    const hoursDifference = timeDifference / (1000 * 60 * 60);
    setIsPast48Hours(hoursDifference >= 48);
  };

  const handleReportPayment = async () => {
    const paymentOperatorData =
      global && global.location && global.location.search ? global.location.search : '';

    try {
      const res = await reportPayment({ token, operatorData: paymentOperatorData });
      const result = res.data.sale.reportPayment;
      if (!result) {
        window.location = import.meta.env.VITE_LANDING_PAGE_URL;
        return null;
      }

      if (result.user && result.user.created) {
        handleIsPast48Hours(result.user.created);
      }

      const type = result.__typename;

      if (type === 'NotReportedPayment') {
        if (result.reportData) {
          result.reportData = result.reportData.map(JSON.parse);
          result.reportData.forEach((data) => window.dataLayer.push(data));

          const userEmail = global.localStorage.getItem('userEmail') || '';

          window.dataLayer.push({
            event: 'conversion',
            email: userEmail,
            sha256_email: sha256(userEmail),
            new_customer: !isPast48Hours,
          });

          trackFirebaseEvent('screen_view', {
            firebase_screen: 'stripe_success',
          });
        }
      } else if (type === 'CanceledPayment') {
        throw new Error('Unexpected response. Typename: CanceledPayment');
      } else if (type === 'ValidationException') {
        toastError(t('app.thankUPage.paymentToken.error'));
        throw new Error('ValidationException');
      }
      setUserEmail(result.user.email);
      return null;
    } catch (err) {
      throw new Error(err);
    }
  };

  useEffect(() => {
    handleReportPayment();
    setTimeout(() => {
      removeAuthCredentials();
    }, 2000); // Delay to ensure the reportPayment is processed before removing auth credentials
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout simpleLayout>
      <div className="page-template-buy full-height7">
        <Container className="py-md-4">
          <Row>
            <Col md={{ size: 6, offset: 3 }}>
              <div className="page-container">
                <div className="thankyou-container">
                  <section>
                    <img
                      src={thankYouPageBg}
                      alt=""
                      className="img-fluid d-none d-md-block mb-xl rounded"
                    />
                    <img
                      src={thankYouPageBgMobile}
                      alt=""
                      className="img-fluid d-md-none mb-xl rounded"
                    />
                    <h1 className="title-large semi-bold text-center mb-xl">
                      {t('app.thankUPage.title')}
                    </h1>
                    <p className="text-center">
                      <span className="text-text-variant-color">
                        {t('app.thankUPage.confirmationSentTo')}
                      </span>
                      <br />
                      <strong>{userEmail}</strong>
                    </p>
                  </section>
                  <hr />
                  <section className="instruction">
                    <h2 className="title-large semi-bold text-center mb-xxl">
                      {t('app.thankUPage.nextStepsHeader')}
                    </h2>
                    <ol>
                      <li>
                        <h3 className="title-medium semi-bold mb-m">
                          {t('app.thankUPage.downloadApp.title')}
                        </h3>
                        <p className="text-text-variant-color">
                          {t('app.thankUPage.downloadApp.description')}
                        </p>
                        <a className="d-inline-block me-l" href={APP_STORE_URL}>
                          <img src={appStore} alt="App store" />
                        </a>
                        <a className="d-inline-block" href={GOOGLE_PLAY_URL}>
                          <img src={googlePlay} alt="Google Play" />
                        </a>
                      </li>
                      <li>
                        <h3 className="title-medium semi-bold mb-m">
                          {t('app.thankUPage.signInToApp.title')}
                        </h3>
                        <p className="text-text-variant-color">
                          {t('app.thankUPage.signInToApp.description')}
                        </p>
                        <img
                          srcSet={`${renderImage('logIn')} 100w`}
                          sizes="50px"
                          className="img-fluid"
                          alt=""
                        />
                      </li>
                      <li>
                        <h3 className="title-medium semi-bold mb-m">
                          {t('app.thankUPage.enjoyApp.title')}
                        </h3>
                        <p className="text-text-variant-color">
                          {t('app.thankUPage.enjoyApp.description')}
                        </p>
                        <img
                          srcSet={`${renderImage('enjoyApp')} 100w`}
                          sizes="50px"
                          className="img-fluid"
                          alt=""
                        />
                      </li>
                    </ol>
                  </section>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

BuyThankYouContainer.propTypes = {
  reportPayment: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  trackFirebaseEvent: PropTypes.func.isRequired,
};

const Placeholder = () => <UniversalPlaceholder simpleLayout fullHeightMinusHeader />;

export default withLoading(BuyThankYouContainer, Placeholder);
